import axiosInstance from '@/app/api/axiosInstance';
import { 
  Note, 
  NoteCreate, 
  NoteUpdate, 
  NoteFilters, 
  NotesListResponse,
  EntityType 
} from '@/app/types/note';

const API_PATH = '/notes';

export const noteService = {
  // Create a new note
  create: async (data: NoteCreate): Promise<Note> => {
    try {
      const response = await axiosInstance.post<Note>(`${API_PATH}/`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to create note:', error);
      throw error;
    }
  },

  // List notes with filters
  list: async (params?: NoteFilters): Promise<NotesListResponse> => {
    try {
      const response = await axiosInstance.get<NotesListResponse>(`${API_PATH}/`, { params });
      return response.data;
    } catch (error) {
      console.error('Failed to list notes:', error);
      throw error;
    }
  },

  // Get notes for a specific entity
  getNotesForEntity: async (entityType: EntityType, entityId: number): Promise<Note[]> => {
    try {
      const response = await axiosInstance.get<Note[]>(
        `${API_PATH}/entity/${entityType}/${entityId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to get notes for ${entityType}/${entityId}:`, error);
      throw error;
    }
  },

  // Get single note by ID
  get: async (noteId: number): Promise<Note> => {
    try {
      const response = await axiosInstance.get<Note>(`${API_PATH}/${noteId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to get note with ID ${noteId}:`, error);
      throw error;
    }
  },

  // Update a note
  update: async (noteId: number, data: NoteUpdate): Promise<Note> => {
    try {
      const response = await axiosInstance.patch<Note>(`${API_PATH}/${noteId}`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to update note with ID ${noteId}:`, error);
      throw error;
    }
  },

  // Toggle pin status
  togglePin: async (noteId: number): Promise<Note> => {
    try {
      const response = await axiosInstance.post<Note>(`${API_PATH}/${noteId}/toggle-pin`);
      return response.data;
    } catch (error) {
      console.error(`Failed to toggle pin for note ${noteId}:`, error);
      throw error;
    }
  },

  // Soft delete a note
  delete: async (noteId: number): Promise<void> => {
    try {
      await axiosInstance.delete(`${API_PATH}/${noteId}`);
    } catch (error) {
      console.error(`Failed to delete note with ID ${noteId}:`, error);
      throw error;
    }
  },

  // Count notes for an entity (useful for indicators)
  countNotesForEntity: async (entityType: EntityType, entityId: number): Promise<number> => {
    try {
      const response = await noteService.list({
        entity_type: entityType,
        entity_id: entityId,
        size: 1,
        page: 1
      });
      return response.total;
    } catch (error) {
      console.error(`Failed to count notes for ${entityType}/${entityId}:`, error);
      return 0;
    }
  }
};

// Export individual functions for convenience
export const createNote = noteService.create;
export const listNotes = noteService.list;
export const getNotesForEntity = noteService.getNotesForEntity;
export const getNote = noteService.get;
export const updateNote = noteService.update;
export const toggleNotePin = noteService.togglePin;
export const deleteNote = noteService.delete;
export const countNotesForEntity = noteService.countNotesForEntity;